import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { cases as casesApi, errorMessage } from '../utils/api';

/**
 * لینک‌های مشاهده‌ی یک پرونده — همان چیزی که با پیامک برای پزشک و بیمار
 * می‌رود.
 *
 * دو مخاطب و دو لینک جدا، چون محتوایشان یکی نیست: صفحه‌ی پزشک انگلیسی
 * است و متن گزارش را دارد، صفحه‌ی بیمار فارسی.
 *
 * دکمه‌ی «ساخت» لینک تازه **نمی‌سازد** اگر لینک زنده‌ای باشد؛ همان را
 * برمی‌گرداند. لینک نو فقط با «جایگزینی» ساخته می‌شود — وگرنه QRای که
 * دیروز روی کاغذ چاپ شده هنوز کار می‌کند ولی هیچ‌کس نمی‌داند کدام است.
 */

const AUDIENCE = [
  { key: 'doctor', label: 'پزشک', hint: 'صفحه‌ی انگلیسی، همراه متن گزارش' },
  { key: 'patient', label: 'بیمار', hint: 'صفحه‌ی فارسی' },
];

export default function ShareLinks({ caseId }) {
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    casesApi
      .shareLinks(caseId)
      .then(({ data }) => setLinks(data))
      .catch(() => setLinks([]));
  }, [caseId]);

  useEffect(load, [load]);

  const make = async (audience, fresh) => {
    setBusy(audience);
    try {
      await casesApi.makeShareLink(caseId, audience, fresh);
      toast.success(fresh ? 'لینک تازه ساخته شد.' : 'لینک آماده است.');
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'لینک ساخته نشد.'));
    } finally {
      setBusy('');
    }
  };

  const revoke = async (link) => {
    setBusy(`revoke-${link.id}`);
    try {
      await casesApi.revokeShareLink(caseId, link.id);
      toast.success('لینک باطل شد. از این پس باز نمی‌شود.');
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'انجام نشد.'));
    } finally {
      setBusy('');
    }
  };

  if (links === null) return null;

  return (
    <div className="slink">
      <div className="slink__head">
        <span className="slink__title">📱 لینک مشاهده‌ی تصویر و گزارش</span>
      </div>

      {AUDIENCE.map(({ key, label, hint }) => {
        const live = links.find((row) => row.audience === key && row.is_live);
        return (
          <div className="slink__row" key={key}>
            <div className="slink__who">
              <strong>{label}</strong>
              <span className="slink__hint">{hint}</span>
            </div>

            {live ? (
              <div className="slink__live">
                <code className="slink__url" dir="ltr" title={live.url}>{live.url}</code>
                <div className="slink__foot">
                  <span className="slink__seen">
                    {live.view_count > 0
                      ? `${live.view_count.toLocaleString('fa-IR')} بار باز شده`
                      : 'هنوز باز نشده'}
                  </span>
                  <div className="slink__acts">
                    <CopyButton text={live.url} />
                    <button
                      type="button" className="link"
                      disabled={busy === key}
                      onClick={() => make(key, true)}
                    >
                      لینک تازه
                    </button>
                    <button
                      type="button" className="link link--danger"
                      disabled={busy === `revoke-${live.id}`}
                      onClick={() => revoke(live)}
                    >
                      باطل کن
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button" className="btn btn--ghost btn--sm"
                disabled={busy === key}
                onClick={() => make(key, false)}
              >
                {busy === key ? '…' : 'ساخت لینک'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    // همان دو مسیرِ PatientLink، و به همان دو دلیل: روی نشانی IP
    // `navigator.clipboard` اصلاً وجود ندارد، و حتی وقتی هست ممکن است
    // پرتاب کند (پنجره‌ی بی‌فوکوس). پس شکستش هم به مسیر دوم می‌رود.
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        return;
      } catch (err) {
        // می‌افتیم روی مسیر دوم
      }
    }

    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();

    if (ok) setCopied(true);
    else toast.error('کپی نشد. لینک را دستی انتخاب و کپی کنید.');
  };

  return (
    <button type="button" className="btn btn--ghost btn--sm" onClick={copy}>
      {copied ? '✓ کپی شد' : '📋 کپی'}
    </button>
  );
}
